/**
 * Bloco "Mais detalhes" do cadastro de caso (E56.F3.T03).
 *
 * Os campos opcionais (solicitante, processo, espécime, contratante, partes)
 * ficam recolhidos por padrão. Eles continuam no formulário e continuam sendo
 * submetidos — o que muda é a hierarquia: criar um caso volta a ser uma ação
 * curta, e boa parte desses campos já vem preenchida da CCB importada.
 *
 * Recolhido é `hidden`, não desmontado: campo desmontado perderia o valor
 * digitado ao fechar o bloco, e o que a CCB preencheu não seria submetido.
 */
import { useId } from 'react';
import { ChevronDown } from 'lucide-react';

import { Blueprint, Button, CardKicker } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

export interface CaseDetailsDisclosureProps {
  children: React.ReactNode;
  /**
   * Controlado pelo pai: a CCB importada abre o bloco (há o que conferir) e um
   * erro de validação em campo daqui também — erro escondido atrás de um bloco
   * fechado é erro que ninguém corrige.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId?: string;
}

export function CaseDetailsDisclosure({
  children,
  open,
  onOpenChange,
  testId = 'novo-caso-mais-detalhes',
}: CaseDetailsDisclosureProps): JSX.Element {
  const painelId = useId();

  return (
    <Blueprint as="section" data-testid={testId} data-open={open} className="p-sc-6">
      <div className="flex flex-wrap items-start justify-between gap-sc-4">
        <div>
          <CardKicker className="block">{ptBR.casos.sectionMaisDetalhes}</CardKicker>
          <p className="mt-1 max-w-xl text-caption text-fg-muted">
            {ptBR.casos.sectionMaisDetalhesHint}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          data-testid={`${testId}-toggle`}
          aria-expanded={open}
          aria-controls={painelId}
          onClick={() => onOpenChange(!open)}
          className="gap-sc-2"
        >
          <ChevronDown
            className={`h-4 w-4 stroke-[1.5] transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
          {open ? ptBR.casos.maisDetalhesHide : ptBR.casos.maisDetalhesShow}
        </Button>
      </div>
      <div
        id={painelId}
        hidden={!open}
        data-testid={`${testId}-painel`}
        className="mt-sc-4 grid grid-cols-1 gap-sc-4 sm:grid-cols-2"
      >
        {children}
      </div>
    </Blueprint>
  );
}
